import React from 'react'
import OwlCarousel from 'react-owl-carousel2'
import PropTypes from 'prop-types'
import owlConfig from '../../owlCarouselConfig'

const SCROLL_END_DELAY = 500

class ScrollableCarousel extends React.Component {
  constructor (props) {
    super(props)

    this.carousel = React.createRef()
    this.scrollCarousel = React.createRef()
    this.scroll = React.createRef()
    this.scrollBarContainer = React.createRef()
    this.scrollBar = React.createRef()

    this.handleScrollMovement = this.handleScrollMovement.bind(this)
    this.handleCarouselChanges = this.handleCarouselChanges.bind(this)
    this.handleOnDrag = this.handleOnDrag.bind(this)
    this.handleOnDragged = this.handleOnDragged.bind(this)
  }

  componentDidMount () {
    this.events = {
      onInitialize: this.handleCarouselChanges,
      onResized: this.handleCarouselChanges,
      onChange: this.handleCarouselChanges,
      onDrag: this.handleOnDrag,
      onDragged: this.handleOnDragged
    }
  }

  handleCarouselChanges (e) {
    const owl = e.relatedTarget
    const lazy = owl._plugins.lazy
    if (owl.settings) {
      let position
      if (e.property && e.property.name === 'position') {
        position = e.property.value
      } else {
        position = lazy._core.current()
      }

      this.updateScrollBar(owl, position)

      if (owl._items.length <= owl.settings.items) {
        this.scroll.current.style.display = 'none'
      } else {
        this.scroll.current.style.display = 'block'
      }
    }
  }

  handleOnDragged (e) {
    const owl = e.relatedTarget
    if (this.draggingHandler) {
      document.removeEventListener('mousemove', this.draggingHandler)
      document.removeEventListener('touchmove', this.draggingHandler)
    }
    this.updateScrollBar(owl)

    clearTimeout(this.hoverRemoveTimeout)

    this.hoverRemoveTimeout = setTimeout(
      this.scroll.current.classList.remove('hover'),
      SCROLL_END_DELAY
    )
  }

  handleOnDrag (e) {
    const owl = e.relatedTarget
    this.draggingHandler = () => {
      this.dragLazyLoad(
        owl,
        owl.closest(owl._drag.stage.current.x, owl._drag.direction)
      )
      const scrollX =
        owl._drag.stage.current.x * -1 * this.scrollTrackRatio(owl)
      const transform = `translate3d(${scrollX}px, 0px, 0px)`
      this.scrollBar.current.style.transition = 'none'
      this.scrollBar.current.style.transform = transform
    }
    document.addEventListener('mousemove', this.draggingHandler)
    document.addEventListener('touchmove', this.draggingHandler)
  }

  updateScrollBar (owl, position = owl.current()) {
    const scrollWidth = `${100 *
      Math.min(owl._items.length, owl.settings.items) /
      owl._items.length}%`
    this.scrollBar.current.style.width = scrollWidth
    this.scrollBar.current.style.transition = `${owl.speed() / 1000}s`

    const x =
      (this.scrollBarContainer.current.clientWidth -
        this.scrollBar.current.clientWidth) /
      (owl._items.length - owl.settings.items) *
      position

    this.xPos = x
    this.scrollBar.current.style.transform = `translate3d(${x}px, 0px, 0px)`
  }

  scrollTrackRatio (owl) {
    const scrollXFirst =
      (this.scrollBarContainer.current.clientWidth -
        this.scrollBar.current.clientWidth) /
      (owl._items.length - owl.settings.items)
    const trackXFirst = owl._widths[0]
    return scrollXFirst / trackXFirst
  }

  dragLazyLoad (owl, position) {
    const lazy = owl._plugins.lazy
    for (const i in [position.position + owl.settings.items - 1]) {
      lazy.load(i)
    }

    if (owl.settings.stagePadding > 0) {
      lazy.load(position + owl.settings.items)
    }
  }

  handleScrollMovement (e) {
    let scrollEndTimeout
    clearTimeout(scrollEndTimeout)
    this.scrollBarContainer.current.style.opacity = '1'
    this.scrollBar.current.style.opacity = '1'

    const pointer = e.type === 'touchstart' ? e.originalEvent.touches[0] : e
    const pointerX = pointer.pageX

    const start = this.xPos
    let delta = null
    let scrollX = null
    let carouselX = null
    let direction = null

    const owl = this.carousel.current.$car

    const moveHandler = moveEvent => {
      moveEvent.preventDefault()
      const newPointer =
        moveEvent.type === 'touchmove'
          ? moveEvent.originalEvent.touches[0]
          : moveEvent
      const newPointerX = newPointer.pageX
      delta = pointerX - newPointerX
      scrollX = Math.max(
        Math.min(
          start - delta,
          this.scrollBarContainer.current.clientWidth -
            this.scrollBar.current.clientWidth
        ),
        0
      )
      carouselX = scrollX / this.scrollTrackRatio(owl) * -1
      this.scrollBar.current.style.transition = 'none'
      this.scrollBar.current.style.transform = `translate3d(${scrollX}px, 0px, 0px)`

      owl.$stage.css({
        transform: `translate3d(${carouselX}px, 0px, 0px)`,
        transition: 'none'
      })

      direction = delta < 0 ? 'left' : 'right'
      this.dragLazyLoad(owl, owl.closest(carouselX, direction))
    }

    window.addEventListener('touchmove', moveHandler)
    window.addEventListener('mousemove', moveHandler)

    const handleScrollDragEnd = () => {
      window.removeEventListener('touchmove', moveHandler)
      window.removeEventListener('mousemove', moveHandler)

      scrollEndTimeout = setTimeout(() => {
        this.scrollBarContainer.current.style.opacity = '1'
        this.scrollBar.current.style.opacity = '1'
      }, SCROLL_END_DELAY)

      owl.speed(owl.settings.dragEndSpeed || owl.settings.smartSpeed)

      if (carouselX === 0) {
        owl.current(0)
      } else {
        owl.current(owl.closest(carouselX, direction))
      }

      owl.invalidate('position')
      owl.update()
      this.updateScrollBar(owl)
    }

    window.addEventListener('touchend', handleScrollDragEnd, { once: true })
    window.addEventListener('mouseup', handleScrollDragEnd, { once: true })
  }

  render () {
    const options = { ...owlConfig, ...this.props.options }
    return (
      <div className='ScrollableCarousel' ref={this.scrollCarousel}>
        <OwlCarousel options={options} events={this.events} ref={this.carousel}>
          {this.props.children}
        </OwlCarousel>
        <div className='carousel-scroll' ref={this.scroll}>
          <div
            className='carousel-scroll-bar-container'
            ref={this.scrollBarContainer}
          >
            <div
              className='carousel-scroll-bar'
              ref={this.scrollBar}
              onTouchStart={this.handleScrollMovement}
              onMouseDown={this.handleScrollMovement}
            />
          </div>
        </div>
      </div>
    )
  }
}

ScrollableCarousel.defaultProps = {
  overflow: true
}

ScrollableCarousel.propTypes = {
  children: PropTypes.node,
  options: PropTypes.object
}

export default ScrollableCarousel
